import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress
} from "@mui/material";
import { FieldSelection } from "./FieldSelection";
import { SkuSummary } from "./SkuSummary";
import { calculateRequiredTiers, convertToLocalPhoneNumber } from "./utils";
import { SelectedFields, DEFAULT_REQUIRED_FIELDS } from "./types";
import { useGooglePlaces } from "../../hooks/useGooglePlaces";
import type {
  SpotItemRevision,
  SpotRating,
  SpotBusinessStatus
} from "../../generatedApi/spotManagerApi";

interface GooglePlaceDetailsModalProps {
  open: boolean;
  onClose: () => void;
  placeId: string;
  onApply: (
    data: Partial<SpotItemRevision> & { website?: string; googleRating?: SpotRating }
  ) => void;
}

export const GooglePlaceDetailsModal: React.FC<GooglePlaceDetailsModalProps> = ({
  open,
  onClose,
  placeId,
  onApply
}) => {
  const [selectedFields, setSelectedFields] = useState<SelectedFields>(DEFAULT_REQUIRED_FIELDS);

  const [error, setError] = useState<string | null>(null);
  const { fetchPlaceDetails, loading } = useGooglePlaces();

  const handleFieldChange = (field: keyof SelectedFields, checked: boolean) => {
    setSelectedFields((prev) => ({
      ...prev,
      [field]: checked
    }));
  };

  const handleFetch = async () => {
    setError(null);

    try {
      const tiers = calculateRequiredTiers(selectedFields);
      if (tiers.length === 0) {
        setError("少なくとも1つのフィールドを選択してください");
        return;
      }

      // APIから詳細情報を取得
      const details = await fetchPlaceDetails(placeId, tiers);

      // 選択されたフィールドのみを抽出
      const filteredData: Partial<SpotItemRevision> & {
        website?: string;
        googleRating?: SpotRating;
      } = {};

      if (selectedFields.name && details.name) {
        filteredData.name = details.name;
      }
      if (selectedFields.formattedAddress && details.address) {
        filteredData.address = details.address;
      }
      if (selectedFields.addressComponents && details.address_component) {
        filteredData.address_component = details.address_component;
      }
      if (selectedFields.location && details.location) {
        filteredData.location = details.location;
      }
      if (selectedFields.googleMapsUri && details.google_cid) {
        filteredData.google_cid = details.google_cid;
      }
      if (selectedFields.internationalPhoneNumber && details.phone_number) {
        // 国際形式（+81）から国内形式（090）に変換
        filteredData.phone_number = convertToLocalPhoneNumber(details.phone_number);
      }
      if (selectedFields.websiteUri && details.website) {
        // websiteを個別フィールドとして返す（親コンポーネント側でxlinksにマージ）
        filteredData.website = details.website;
      }
      if (selectedFields.regularOpeningHours && details.opening_hours) {
        // opening_hoursは配列で返ってくるので改行区切りの文字列に変換
        filteredData.opening_hours = details.opening_hours.join("\n");
      }
      if (
        (selectedFields.rating || selectedFields.userRatingCount) &&
        details.rating &&
        details.rating.rating !== null &&
        details.rating.rating !== undefined &&
        details.rating.rating_count !== null &&
        details.rating.rating_count !== undefined
      ) {
        // googleRatingを個別フィールドとして返す（親コンポーネント側でrating配列にマージ）
        filteredData.googleRating = {
          source_name: details.rating.source_name || "googlemaps",
          rating: details.rating.rating,
          rating_count: details.rating.rating_count
        };
      }
      if (selectedFields.businessStatus && details.business_status) {
        filteredData.business_status = details.business_status as SpotBusinessStatus;
      }
      if (selectedFields.parkingOptions && details.parking) {
        filteredData.parking = details.parking;
      }

      // 取得したデータを親コンポーネントに反映
      onApply(filteredData);

      // 成功メッセージを表示
      alert("Google情報を取得しました");

      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Google情報の取得に失敗しました";
      setError(errorMessage);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Google詳細情報取得</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <FieldSelection selectedFields={selectedFields} onChange={handleFieldChange} />
        <SkuSummary selectedFields={selectedFields} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          キャンセル
        </Button>
        <Button
          onClick={handleFetch}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          取得
        </Button>
      </DialogActions>
    </Dialog>
  );
};
